export type UserRights = Set<number>;

export type AccessLevel = boolean | number | Set<number> | ((userRights: UserRights) => boolean) | AccessLevel[];

export type Dictionary = Record<string, number>;

export function check(accessLevel: AccessLevel, userRights: UserRights): boolean {
  if (typeof accessLevel === 'boolean') return accessLevel;
  if (typeof accessLevel === 'function') return accessLevel(userRights);

  if (Array.isArray(accessLevel)) {
    // If `accessLevel` is an array (i.e. a logical OR), check for at least one match
    return accessLevel.some((entry) => check(entry, userRights));
  }

  if (accessLevel instanceof Set) {
    // If `accessLevel` is `Set` (i.e. a logical AND), we check if the user has all rights
    return [...accessLevel].every((level) => userRights.has(level));
  }

  return userRights.has(accessLevel);
}

export function mergeMask(dict: Dictionary) {
  return (arr: string[]): UserRights => {
    const resultSet: UserRights = new Set();
    arr.forEach((item) => {
      resultSet.add(dict[item]);
    });
    return resultSet;
  };
}

/**
 * Helper function
 * @function
 * @name createPolicies
 * @param dict object dictionary of all available rights
 * @param items array of rights for this instance
 *
 * */

export function createPolicies(dict: Dictionary, items: Array<string | string[]>): AccessLevel[] {
  return items.map((entry) => {
    if (Array.isArray(entry)) {
      // If an array (logical AND) is passed, we create a Set where all elements must be present
      return new Set(entry.map((right) => dict[right]));
    }
    // If it's a single rule, just add it as a separate element
    return dict[entry];
  });
}

export function makeDictionary(arr: string[]): Dictionary {
  return arr.reduce<Dictionary>((a, e, i) => ({ ...a, [e]: i }), {});
}

export function arrayFilter(restriction: UserRights) {
  return (f: { restriction: AccessLevel }) => check(f.restriction, restriction);
}

export function arrayMap<K extends string>(restriction: UserRights, fieldName: K) {
  // A missing field means the default: not granted (e.g. an item without `disabled` is enabled).
  return <T extends Partial<Record<K, AccessLevel>>>(m: T) => ({
    ...m,
    [fieldName]: check(m[fieldName] ?? false, restriction),
  });
}

export function advancedRights<A extends unknown[]>(rules: (...args: A | []) => Record<string, () => boolean>) {
  const pa = Object.keys(rules());
  const dictionary = makeDictionary(pa);
  return {
    dictionary,
    restriction(...args: A): UserRights {
      const r = rules(...args);
      const userRights: UserRights = new Set();
      pa.forEach((v) => {
        if (r[v]()) {
          userRights.add(dictionary[v]);
        }
      });
      return userRights;
    },
    policy: (...arr: Array<string | string[]>) => createPolicies(dictionary, arr),
    paramsName: pa.reduce((a, v) => Object.assign(a, { [v]: v }), { [pa[0]]: pa[0] }),
  };
}
